/**
 * use-permission-center — dane Centrum uprawnień (F24 Kawałek 1).
 *
 * Osobny plik od `use-permissions.ts`, bo tamten obsługuje STARY model (role z listą kodów,
 * dosypka per użytkownik) i będzie się kurczył, a nie rósł. Tu żyje nowy kontrakt: drzewo
 * obiektów + reguły podmiotu + zapis komórkowy.
 *
 * Zapis jest komórkowy i unieważnia tylko reguły DOTKNIĘTEGO podmiotu — drzewo (typy, kategorie)
 * nie zmienia się od nadania prawa, więc nie ma powodu go przeładowywać.
 */

import type { PermissionAction, PermissionEffect, PermissionObjectKind } from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

// ============================================================================
// Typy kontraktu
// ============================================================================

export interface PermissionTreeNode {
  kind:
    | 'kartoteka'
    | 'doc_class'
    | 'doc_type'
    | 'category'
    | 'process'
    | 'process_any'
    | 'process_step';
  key: string;
  id?: string;
  label?: string;
  codeNamespace: string;
  actions: PermissionAction[];
  origin?: 'global' | 'local';
  /** Kartoteka modułu zainstalowana przez ITE — dla tenanta systemowa (zakładka „Kartoteki"). */
  installedByIte?: boolean;
  /**
   * Tylko przy etapie procesu: czy dokument może na nim stanąć. `true` dla węzła decyzyjnego
   * i końcowego (sprawa zakończona stoi na etapie końcowym), `false` dla startu i rozgałęzień —
   * wiersz jest wtedy oznaczony jako przelotowy.
   */
  stageHoldsRecord?: boolean;
  children?: PermissionTreeNode[];
}

export interface PermissionRuleRow {
  subjectKind: 'role' | 'team' | 'user';
  subjectId: string;
  permissionCode: string | null;
  action: PermissionAction | null;
  objectKind: PermissionObjectKind;
  objectKey: string | null;
  objectId: string | null;
  effect: PermissionEffect;
}

export interface RuleChange {
  code: string;
  objectKind: PermissionObjectKind;
  objectKey?: string | null;
  objectId?: string | null;
  effect: PermissionEffect | 'inherit';
}

export interface TeamRow {
  id: string;
  /** Zespół należy do jednej firmy (jak rola). */
  companyId: string;
  /** Linia zespołu — kopie tego samego zespołu w różnych firmach dzielą ją. */
  teamKey: string;
  name: string;
  description: string | null;
  sortOrder: number;
  memberCount: number;
}

// ============================================================================
// Klucze cache
// ============================================================================

export const PERMISSION_TREE_KEY = (companyId: string) =>
  ['permissions', 'tree', companyId] as const;
export const PERMISSION_RULES_KEY = (companyId: string, subjectKind: string, subjectId: string) =>
  ['permissions', 'rules', companyId, subjectKind, subjectId] as const;
/** Prefiks list zespołów — unieważnia listy wszystkich firm naraz. */
export const TEAMS_KEY = ['teams', 'list'] as const;
export const TEAMS_FOR_COMPANY_KEY = (companyId: string) => [...TEAMS_KEY, companyId] as const;

// ============================================================================
// Hooki
// ============================================================================

/** Drzewo obiektów firmy: kartoteki → klasy → typy / kategorie. */
export function usePermissionTree(companyId: string | null) {
  return useQuery<{ companyId: string; nodes: PermissionTreeNode[] }>({
    queryKey: PERMISSION_TREE_KEY(companyId ?? 'none'),
    queryFn: async () => {
      const res = await api.get<{ companyId: string; nodes: PermissionTreeNode[] }>(
        `/permissions/tree?companyId=${companyId}`,
      );
      return res.data;
    },
    enabled: Boolean(companyId),
    // Drzewo zmienia się rzadko (nowy typ dokumentu, nowa kategoria) — trzymamy dłużej niż reguły.
    staleTime: 60_000,
  });
}

/** Rola albo zespół, z którego może płynąć uprawnienie osoby. */
export interface InheritedSubject {
  kind: 'role' | 'team';
  id: string;
  name: string;
}

export interface PermissionRulesResponse {
  rules: PermissionRuleRow[];
  /** Tylko dla podmiotu „osoba": jej rola i zespoły (nazwy do pokazania „skąd"). */
  inheritedSubjects?: InheritedSubject[];
  /** Reguły tych podmiotów — panel liczy z nich wynik tym samym kodem, co serwer. */
  inheritedRules?: PermissionRuleRow[];
}

/** Reguły JEDNEGO podmiotu w firmie. */
export function usePermissionRules(
  companyId: string | null,
  subject: { kind: 'role' | 'team' | 'user'; id: string } | null,
) {
  return useQuery<PermissionRulesResponse>({
    queryKey: PERMISSION_RULES_KEY(
      companyId ?? 'none',
      subject?.kind ?? 'none',
      subject?.id ?? 'none',
    ),
    queryFn: async () => {
      const res = await api.get<PermissionRulesResponse>(
        `/permissions/rules?companyId=${companyId}&subjectKind=${subject!.kind}&subjectId=${subject!.id}`,
      );
      return res.data;
    },
    enabled: Boolean(companyId && subject),
    staleTime: 10_000,
  });
}

/** Zapis zmian komórkowych. */
export function useUpdatePermissionRules(
  companyId: string | null,
  subject: { kind: 'role' | 'team' | 'user'; id: string } | null,
) {
  const qc = useQueryClient();
  return useMutation<{ applied: number; removed: number }, unknown, RuleChange[]>({
    mutationFn: async (changes) => {
      const res = await api.put<{ applied: number; removed: number }>('/permissions/rules', {
        companyId,
        subjectKind: subject!.kind,
        subjectId: subject!.id,
        changes,
      });
      return res.data;
    },
    onSuccess: () => {
      if (!companyId || !subject) return;
      void qc.invalidateQueries({
        queryKey: PERMISSION_RULES_KEY(companyId, subject.kind, subject.id),
      });
    },
  });
}

/**
 * Role DANEJ FIRMY — role są per firma (F24), więc lista podmiotów zmienia się razem z wyborem
 * firmy. Wzorce tenanta (bez firmy) trasa odfiltrowuje, bo nie da się ich nikomu przypisać.
 */
export function useRolesForCenter(companyId: string | null) {
  return useQuery<{ roleTemplates: Array<{ id: string; name: string; roleKey: string }> }>({
    queryKey: ['permissions', 'center', 'roles', companyId ?? 'none'] as const,
    queryFn: async () => {
      const res = await api.get<{
        roleTemplates: Array<{ id: string; name: string; roleKey: string }>;
      }>(`/role-templates?companyId=${companyId}`);
      return res.data;
    },
    enabled: Boolean(companyId),
    staleTime: 30_000,
  });
}

/** Firmy tenanta — kontekst całego ekranu. */
export function useCompaniesForCenter() {
  return useQuery<{
    companies: Array<{ id: string; legalName: string; shortName: string | null }>;
  }>({
    queryKey: ['permissions', 'center', 'companies'] as const,
    queryFn: async () => {
      const res = await api.get<{
        companies: Array<{ id: string; legalName: string; shortName: string | null }>;
      }>('/companies?limit=200');
      return res.data;
    },
    staleTime: 60_000,
  });
}

/** Osoby tenanta — podmiot „indywidualne". */
export function useUsersForCenter() {
  return useQuery<{ users: Array<{ id: string; email: string; displayName: string | null }> }>({
    queryKey: ['permissions', 'center', 'users'] as const,
    queryFn: async () => {
      const res = await api.get<{
        users: Array<{ id: string; email: string; displayName: string | null }>;
      }>('/users?limit=200');
      return res.data;
    },
    staleTime: 60_000,
  });
}

/** Zespoły wybranej firmy — zespoły są per firma, jak role. Bez firmy nic nie pobiera. */
export function useTeams(companyId: string | null) {
  return useQuery<{ teams: TeamRow[] }>({
    queryKey: TEAMS_FOR_COMPANY_KEY(companyId ?? 'none'),
    queryFn: async () => {
      const res = await api.get<{ teams: TeamRow[] }>(`/teams?companyId=${companyId}`);
      return res.data;
    },
    enabled: Boolean(companyId),
    staleTime: 30_000,
  });
}

/**
 * Katalog uprawnień tenanta — kody SPOZA drzewa kartotek (procesy, pulpit, ustawienia,
 * integracje). Trasa zwraca wyłącznie kody modułów aktywnych u tenanta, więc lista nie pokazuje
 * praw do rzeczy, których klient nie ma wykupionych.
 */
export interface CatalogDefinitionRow {
  code: string;
  moduleCode: string;
  name: string | null;
  description: string | null;
  category: string | null;
  isAdminOnly: boolean;
}

export function usePermissionDefinitions() {
  return useQuery<{ definitions: CatalogDefinitionRow[] }>({
    queryKey: ['permissions', 'center', 'definitions'] as const,
    queryFn: async () => {
      const res = await api.get<{ definitions: CatalogDefinitionRow[] }>(
        '/permissions/definitions',
      );
      return res.data;
    },
    // Katalog zmienia się przy wydaniu systemu, nie w trakcie pracy.
    staleTime: 300_000,
  });
}

/**
 * Moduły własne tenanta — tylko po to, żeby nazwać grupy na zakładce „Moduły". Kody uprawnień
 * niosą sam slug (`bhp_wypadki`), a to nie jest nazwa, po której ktokolwiek rozpozna moduł.
 */
export function useCustomModules() {
  return useQuery<{ modules: Array<{ id: string; slug: string; name: string }> }>({
    queryKey: ['permissions', 'center', 'custom-modules'] as const,
    queryFn: async () => {
      const res = await api.get<{ modules: Array<{ id: string; slug: string; name: string }> }>(
        '/modules',
      );
      return res.data;
    },
    staleTime: 300_000,
  });
}

// ============================================================================
// Menu per user (zakładka „Menu") — F24. Widoczność MENU MODUŁÓW (kolorowe ikony sidebara).
// ============================================================================

export interface MenuModuleOption {
  code: string;
  name: string;
  icon: string | null;
}

/** Wszystkie menu modułów tenanta (Systemowe, BHP, Urlopy…) — lista do zakładki „Menu". */
export function useAvailableMenuModules() {
  return useQuery<{ modules: MenuModuleOption[] }>({
    queryKey: ['menu', 'available-modules'] as const,
    queryFn: async () => {
      const res = await api.get<{ modules: MenuModuleOption[] }>('/menu/available-modules');
      return res.data;
    },
    staleTime: 30_000,
  });
}

/** Podmiot zakładki „Menu" — user / rola / zespół (wzór jak w regułach uprawnień). */
export type MenuSubject = { kind: 'role' | 'team' | 'user'; id: string };

/** Wskazanie menu domyślnego (gwiazdka) — moduł ALBO custom layout. */
export interface MenuDefaultRef {
  kind: 'module' | 'layout';
  ref: string;
}

export interface MenuAssignmentState {
  /** Kody dostępnych menu modułów. */
  modules: string[];
  /** Id dostępnych custom layoutów. */
  layouts: string[];
  /** Menu domyślne (otwierane po zalogowaniu) — jedno, wspólne dla obu rodzajów. */
  default: MenuDefaultRef | null;
  /** false = brak nadpisania = podmiot widzi wszystkie menu. */
  hasOverride: boolean;
}

const MENU_ASSIGNMENTS_KEY = (subjectKind: string, subjectId: string, companyId: string) =>
  ['menu', 'assignments', subjectKind, subjectId, companyId] as const;

/**
 * Menu (moduły + layouty) przypisane podmiotowi (user/rola/zespół) W FIRMIE — menu jest per firma
 * (migracja 0175). Bez firmy nic nie pobieramy.
 */
export function useMenuAssignments(subject: MenuSubject | null, companyId: string | null) {
  return useQuery<MenuAssignmentState>({
    queryKey: MENU_ASSIGNMENTS_KEY(
      subject?.kind ?? 'none',
      subject?.id ?? 'none',
      companyId ?? 'none',
    ),
    queryFn: async () => {
      const res = await api.get<MenuAssignmentState>(
        `/menu/assignments?subjectKind=${subject!.kind}&subjectId=${subject!.id}&companyId=${companyId}`,
      );
      return res.data;
    },
    enabled: Boolean(subject) && Boolean(companyId),
    staleTime: 10_000,
  });
}

/** Zapis widoczności menu podmiotu w firmie (replace-all: moduły + layouty + domyślne). */
export function useUpdateMenuAssignments(subject: MenuSubject | null, companyId: string | null) {
  const qc = useQueryClient();
  return useMutation<
    MenuAssignmentState,
    unknown,
    { modules: string[]; layouts: string[]; default: MenuDefaultRef | null }
  >({
    mutationFn: async (state) => {
      const res = await api.put<MenuAssignmentState>('/menu/assignments', {
        subjectKind: subject!.kind,
        subjectId: subject!.id,
        companyId,
        modules: state.modules,
        layouts: state.layouts,
        default: state.default,
      });
      return res.data;
    },
    onSuccess: () => {
      if (!subject || !companyId) return;
      void qc.invalidateQueries({
        queryKey: MENU_ASSIGNMENTS_KEY(subject.kind, subject.id, companyId),
      });
    },
  });
}
